/**
 * SettingsPanel — 微课外观设置面板
 * 包含主题选择、字体选择、转场效果、字号缩放、配色设置
 */

import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Check, RotateCcw, Save, Volume2, Loader2 } from 'lucide-react'
import { MICRO_THEMES, FONT_FAMILIES, TRANSITIONS } from '@ai-tutor/shared'
import type { LessonSettings } from '@/store/lessonStore'

export interface SettingsPanelProps {
  settings: LessonSettings
  dirty: boolean
  saving: boolean
  ttsLoading: boolean
  onSettingsChange: (partial: Partial<LessonSettings>) => void
  onSave: () => void
  onReset: () => void
  onGenerateTTS: () => void
}

export function SettingsPanel({
  settings,
  dirty,
  saving,
  ttsLoading,
  onSettingsChange,
  onSave,
  onReset,
  onGenerateTTS,
}: SettingsPanelProps) {
  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-4 space-y-5">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <span className="w-1 h-4 rounded bg-blue-500" />
          外观设置
        </h3>

        {/* Theme */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">主题</label>
          <div className="grid grid-cols-2 gap-1.5">
            {MICRO_THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => onSettingsChange({ theme: theme.id })}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-xs transition-all ${
                  settings.theme === theme.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 font-medium'
                    : 'border-slate-200 hover:border-slate-400 dark:border-slate-700'
                }`}
              >
                <span
                  className="w-3.5 h-3.5 rounded-full border shrink-0"
                  style={{ background: theme.preview.bg }}
                />
                <span className="truncate">{theme.name}</span>
                {settings.theme === theme.id && (
                  <Check className="w-3 h-3 text-blue-500 ml-auto shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Font Family */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">字体</label>
          <Select
            value={FONT_FAMILIES.find((f) => f.stack === settings.fontFamily)?.id || 'system-ui'}
            onValueChange={(id) => {
              const font = FONT_FAMILIES.find((f) => f.id === id)
              if (font) onSettingsChange({ fontFamily: font.stack })
            }}
          >
            <SelectTrigger className="text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((font) => (
                <SelectItem key={font.id} value={font.id} className="text-xs">
                  {font.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Transition */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">转场效果</label>
          <Select
            value={settings.transition}
            onValueChange={(val) => onSettingsChange({ transition: val })}
          >
            <SelectTrigger className="text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSITIONS.map((t) => (
                <SelectItem key={t} value={t} className="text-xs capitalize">
                  {t === 'none' ? '无' : t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Font Scale */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">
            字号缩放 <span className="text-slate-400">({settings.fontScale.toFixed(1)}x)</span>
          </label>
          <Slider
            value={[settings.fontScale]}
            min={0.5}
            max={2.0}
            step={0.1}
            onValueChange={(vals) => onSettingsChange({ fontScale: vals[0] })}
          />
        </div>

        {/* Custom Colors */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">配色</label>
          <div className="space-y-2">
            <ColorRow
              label="主色"
              value={settings.customColors.primary}
              onChange={(val) =>
                onSettingsChange({
                  customColors: { ...settings.customColors, primary: val },
                })
              }
            />
            <ColorRow
              label="强调色"
              value={settings.customColors.accent}
              onChange={(val) =>
                onSettingsChange({
                  customColors: { ...settings.customColors, accent: val },
                })
              }
            />
            <ColorRow
              label="背景色"
              value={settings.customColors.bg}
              onChange={(val) =>
                onSettingsChange({
                  customColors: { ...settings.customColors, bg: val },
                })
              }
            />
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-2 border-t">
          <Button
            onClick={onSave}
            disabled={!dirty || saving}
            size="sm"
            className="w-full"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1" />
            )}
            保存设置
          </Button>

          <Button
            onClick={onReset}
            disabled={!dirty}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            恢复
          </Button>

          <Button
            onClick={onGenerateTTS}
            disabled={ttsLoading}
            variant="outline"
            size="sm"
            className="w-full"
          >
            {ttsLoading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Volume2 className="w-3.5 h-3.5 mr-1" />
            )}
            生成语音
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Color Row ───────────────────────────────────────────────────

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (val: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-14 shrink-0">{label}</span>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded border border-slate-200 cursor-pointer shrink-0"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-700 dark:bg-slate-800"
      />
    </div>
  )
}
